/**
 * テンプレート管理用カスタムフック
 */

import type { EmailTemplate, EmailTemplateId } from "@repo/types";
import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/api-client";

// ============================================
// 型定義
// ============================================

interface UseTemplatesResult {
  templates: EmailTemplate[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface UseTemplateResult {
  template: EmailTemplate | null;
  isLoading: boolean;
  error: string | null;
  update: (data: { subject: string; htmlContent: string; textContent: string }) => Promise<boolean>;
  reset: () => Promise<boolean>;
  refresh: () => Promise<void>;
}

interface PreviewResult {
  subject: string;
  html: string;
  text: string;
}

// ============================================
// テンプレート一覧フック
// ============================================

export function useTemplates(): UseTemplatesResult {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.api.admin.templates.$get();

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError((errorData as { error?: string }).error || "テンプレート一覧の取得に失敗しました");
      }
    } catch (_err) {
      setError("通信エラーが発生しました");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return {
    templates,
    isLoading,
    error,
    refresh: fetchTemplates,
  };
}

// ============================================
// テンプレート詳細フック
// ============================================

export function useTemplate(templateId: EmailTemplateId | null): UseTemplateResult {
  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplate = useCallback(async () => {
    if (!templateId) {
      setTemplate(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.api.admin.templates[":templateId"].$get({
        param: { templateId },
      });

      if (response.ok) {
        const data = await response.json();
        setTemplate(data as EmailTemplate);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError((errorData as { error?: string }).error || "テンプレートの取得に失敗しました");
      }
    } catch (_err) {
      setError("通信エラーが発生しました");
    } finally {
      setIsLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  // 更新
  const update = useCallback(
    async (data: { subject: string; htmlContent: string; textContent: string }) => {
      if (!templateId) return false;

      try {
        const response = await apiClient.api.admin.templates[":templateId"].$put({
          param: { templateId },
          json: data,
        });

        if (response.ok) {
          const updated = await response.json();
          setTemplate(updated as EmailTemplate);
          return true;
        }
        return false;
      } catch {
        return false;
      }
    },
    [templateId],
  );

  // リセット
  const reset = useCallback(async () => {
    if (!templateId) return false;

    try {
      const response = await apiClient.api.admin.templates[":templateId"].reset.$post({
        param: { templateId },
      });

      if (response.ok) {
        const updated = await response.json();
        setTemplate(updated as EmailTemplate);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [templateId]);

  return {
    template,
    isLoading,
    error,
    update,
    reset,
    refresh: fetchTemplate,
  };
}

// ============================================
// プレビュー生成
// ============================================

export async function generatePreview(
  templateId: EmailTemplateId,
  variables: Record<string, string>,
): Promise<PreviewResult | null> {
  try {
    const response = await apiClient.api.admin.templates[":templateId"].preview.$post({
      param: { templateId },
      json: { variables },
    });

    if (response.ok) {
      return (await response.json()) as PreviewResult;
    }
    return null;
  } catch {
    return null;
  }
}
